/**
 * Error Translator Utility
 *
 * Maps known backend English error messages to i18n keys so that
 * error messages are displayed in the user's selected locale.
 *
 * Usage:
 *   import { translateError } from '@/utils/errorTranslator';
 *   const localizedMessage = translateError(backendErrorString, t);
 *
 * Where `t` is the vue-i18n translation function (from useI18n() or this.$t).
 */

/**
 * Mapping from known backend error strings to i18n keys.
 * Keys are exact matches of strings returned by the backend API.
 */
const BACKEND_ERROR_MAP = {
  // Password validation (from passwordUtils.js)
  'Password must be at least 8 characters long': 'errors.passwordMinLength',
  'Password must be at least 8 characters': 'errors.passwordMinLength',
  'Password must be less than 100 characters': 'errors.passwordMaxLength',
  'Password must contain at least one uppercase letter': 'errors.passwordUppercase',
  'Password must contain at least one lowercase letter': 'errors.passwordLowercase',
  'Password must contain at least one number': 'errors.passwordNumber',
  'Password must contain at least one special character': 'errors.passwordSpecialChar',
  'Password must include uppercase, lowercase, number, and special character': 'errors.passwordRequirementsCombined',
  'Password must contain uppercase, lowercase, number, and special character': 'errors.passwordRequirementsCombined',
  'Passwords do not match': 'errors.passwordMismatch',
  'Password is required': 'errors.passwordRequired',

  // Email validation
  'Email is required': 'errors.emailRequired',
  'Invalid email format': 'errors.emailInvalid',
  'Email already exists': 'errors.emailExists',

  // Username validation
  'Username is required': 'errors.usernameRequired',
  'Username must be at least 3 characters long': 'errors.usernameMinLength',
  'Username must be at least 3 characters': 'errors.usernameMinLength',
  'Username must be less than 30 characters': 'errors.usernameMaxLength',
  'Username can only contain letters, numbers, underscores, and hyphens': 'errors.usernameInvalidChars',
  'Username can only contain letters, numbers, and underscores': 'errors.usernameInvalidChars',
  'Username already exists': 'errors.usernameExists',

  // Auth errors (from authConfig.js)
  'Invalid email or password': 'errors.invalidCredentials',
  'Too many attempts, please try again later': 'errors.rateLimited',
  'Password does not meet security requirements': 'errors.weakPassword',

  // Misc
  'Reset token is missing': 'errors.resetTokenMissing',
  'Failed to reset password': 'errors.resetFailed',
  'Invalid invitation link': 'errors.invalidInvitationLink',
  'Invalid or expired invitation': 'errors.invalidOrExpiredInvitation',
  'Failed to validate invitation': 'errors.failedToValidateInvitation',
  'Failed to create account': 'errors.failedToCreateAccount',
  'Failed to create account. Please try again.': 'errors.failedToCreateAccount',
  'First name is required': 'errors.firstNameRequired',
  'Last name is required': 'errors.lastNameRequired',
};

/**
 * Translate a backend error message to the current locale.
 *
 * @param {string} message - The raw error message (usually English from backend)
 * @param {Function} t - The vue-i18n translation function
 * @returns {string} The translated message, or the original message if no mapping found
 */
export function translateError(message, t) {
  if (!message || typeof message !== 'string') {
    return message || '';
  }

  const trimmed = message.trim();
  const i18nKey = BACKEND_ERROR_MAP[trimmed];

  if (i18nKey && typeof t === 'function') {
    return t(i18nKey);
  }

  // Fallback: return the original message
  return trimmed;
}

/**
 * Check if a message is a known backend error that can be translated.
 *
 * @param {string} message - The error message to check
 * @returns {boolean}
 */
export function isKnownBackendError(message) {
  if (!message || typeof message !== 'string') return false;
  return message.trim() in BACKEND_ERROR_MAP;
}

export default { translateError, isKnownBackendError };
